import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import clsx from 'clsx'
import { List, ListItem } from 'chaoskit/src/components'
import { StylesIconVariables } from 'chaoskit/src/components/Icon'
import { Highlight, connectRefinementList } from 'react-instantsearch-dom'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'
import { getComputedLineHeight } from 'chaoskit/src/assets/styles/utility/misc'

import Icon from '~components/Icon'
import useAuthor from '~hooks/useAuthor'
import Image from '~components/Image'
import ResourceCard from '~components/ResourceCard'

export const StylesResourcesVariables = {
  refine: {
    borderSize: 2,
  },
}

const refineLinkStyles = (theme) => ({
  position: 'relative',
  display: 'block',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontSize: theme.fontSize.small,
  paddingRight: `calc((${theme.space.xsmall}px / 2) + (${StylesIconVariables.small} * 2))`,
  paddingLeft: theme.space.small,
  borderColor: 'transparent',
  borderStyle: 'solid',
  borderWidth: StylesResourcesVariables.refine.borderSize,
  transition: `border-color ${theme.timing.base} ${theme.transition.base}`,
  borderRadius:
    getComputedLineHeight({
      fontSize: theme.fontSize.small,
      lineHeight: theme.lineHeight.small,
    }) +
    theme.space.small / 2,
  color: theme.contrast.base,

  '&:hover, &:focus, &.is-active': {
    color: theme.contrast.base,
    borderColor: theme.contrast.muted,
  },

  '&.is-active': {
    borderColor: theme.contrast.base,
    fontWeight: theme.fontWeight.bold,
  },
})

function findMicrosite(filePath) {
  if (/(src\/chaos-monkey)/.test(filePath)) {
    return 'Chaos Monkey'
  }

  if (/(src\/site-reliability-engineering)/.test(filePath)) {
    return 'Site Reliability Engineering'
  }

  if (/(src\/reliability)/.test(filePath)) {
    return 'Reliability'
  }

  return null
}

function getResourceInfo(hit) {
  const isObject = typeof hit === 'object'
  const switchTest = isObject ? hit.articleLabel : hit

  switch (switchTest) {
    case 'Blog Posts':
      return {
        icon: 'resources__blog',
        meta: isObject && hit.author,
        metaSub:
          isObject &&
          hit.date &&
          new Date(hit.date * 1000).toLocaleString('en', {
            dateStyle: 'long',
          }),
      }
    case 'Books':
      return {
        icon: 'resources__book',
      }
    case 'Case Studies':
      return {
        icon: 'resources__case-study',
        metaLogo: isObject && hit.logo,
      }
    case 'Docs':
      return {
        icon: 'resources__doc',
        meta: isObject && hit.section,
      }
    case 'Media':
      return {
        icon: 'resources__media',
        meta: isObject && hit.company,
        metaSub:
          isObject &&
          hit.date &&
          new Date(hit.date * 1000).toLocaleString('en', {
            dateStyle: 'long',
          }),
      }
    case 'Microsites':
      return {
        icon: 'resources__microsite',
        meta: isObject && findMicrosite(hit.fileAbsolutePath),
      }
    case 'Podcasts':
      return {
        icon: 'resources__podcast',
      }
    case 'Talks':
      return {
        icon: 'resources__talk',
      }
    case 'Tutorials':
      return {
        icon: 'resources__tutorial',
        meta: isObject && hit.author,
        metaSub:
          isObject &&
          hit.date &&
          new Date(hit.date * 1000).toLocaleString('en', {
            dateStyle: 'long',
          }),
      }
    case 'Webinars':
      return {
        icon: 'resources__webinar',
        meta:
          isObject && !hit.onDemand && hit.date
            ? new Date(hit.date * 1000).toLocaleString('en', {
                dateStyle: 'long',
              })
            : 'On-Demand',
        metaSub: isObject && !hit.onDemand && hit.time ? hit.time : null,
      }
    case 'Events':
      return {
        icon: 'resources__event',
        meta: new Date(hit.date * 1000).toLocaleString('en', {
          dateStyle: 'long',
        }),
        metaSub: hit.time,
      }
    case 'Whitepapers':
      return {
        icon: 'resources__whitepaper',
      }
    default:
      return {
        icon: 'resources__default',
      }
  }
}

export const Hit = ({ hit }) => {
  const theme = useTheme()
  const author = useAuthor(hit.author)

  return (
    <ResourceCard
      data={hit}
      title={<Highlight tagName="mark" attribute="title" hit={hit} />}
      bottomContent={
        <div
          css={{
            marginTop: theme.space.base,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gridGap: theme.space.base,
            alignItems: 'center',
          }}
        >
          {hit.author ? (
            <Image
              css={{
                width: StylesAvatarVariables(theme).size.base,
              }}
              src={author.frontmatter.photo}
              alt={author.frontmatter.name}
            />
          ) : (
            <div
              css={{
                width: StylesAvatarVariables(theme).size.base,
                height: StylesAvatarVariables(theme).size.base,
                borderRadius: theme.borderRadius.rounded,
                background: theme.color.dark.base,
                color: theme.contrast.base,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Icon
                css={{
                  fontSize: theme.fontSize.large,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
                icon={getResourceInfo(hit).icon}
              />
            </div>
          )}
          <div>
            {getResourceInfo(hit).meta && (
              <div
                css={{
                  color: theme.fontColor.base,
                  fontSize: theme.fontSize.medium,
                  lineHeight: theme.lineHeight.medium,
                  fontWeight: theme.fontWeight.bold,
                }}
              >
                {getResourceInfo(hit).meta}
              </div>
            )}
            {getResourceInfo(hit).metaLogo && (
              <Image
                src={getResourceInfo(hit).metaLogo}
                alt=""
                css={{ maxHeight: theme.height.base }}
              />
            )}
            {getResourceInfo(hit).metaSub && (
              <div
                css={{
                  color: theme.fontColor.muted,
                  fontSize: theme.fontSize.small,
                }}
              >
                {getResourceInfo(hit).metaSub}
              </div>
            )}
          </div>
        </div>
      }
    />
  )
}

Hit.propTypes = {
  hit: PropTypes.object,
}

const TypesRefinementList = ({ items, refine, createURL }) => {
  const theme = useTheme()

  if (items.length === 0) return null

  return (
    <List
      space="small"
      css={{
        [theme.mq.small]: {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },

        [theme.mq.medium]: {
          gridTemplateColumns: 'repeat(4, 1fr)',
        },
      }}
    >
      {items.map((item) => (
        <ListItem
          css={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          key={item.label}
        >
          <a
            css={[
              refineLinkStyles(theme),
              {
                height: theme.height.small,
                lineHeight: `${
                  theme.height.small -
                  StylesResourcesVariables.refine.borderSize * 2
                }px`,
              },
            ]}
            className={clsx({
              [theme.settings.classes.active]: item.isRefined,
            })}
            href={createURL(item.value)}
            onClick={(event) => {
              event.preventDefault()
              refine(item.value)
            }}
            title={`${item.isRefined ? 'Remove' : 'Add'} ${item.label}`}
          >
            <Icon
              size="large"
              css={{ marginRight: theme.space.small }}
              icon={getResourceInfo(item.label).icon}
            />{' '}
            {item.label}
            {item.isRefined && (
              <Icon
                icon="close"
                size="small"
                css={{
                  position: 'absolute',
                  top: '50%',
                  right: StylesIconVariables.small,
                  transform: 'translateY(-50%)',
                }}
              />
            )}
          </a>
        </ListItem>
      ))}
    </List>
  )
}

TypesRefinementList.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
}

export const CustomTypesRefinementList = connectRefinementList(
  TypesRefinementList
)

const TopicsRefinementList = ({ items, refine, createURL }) => {
  const theme = useTheme()

  if (items.length === 0) return null

  return (
    <List
      space="small"
      css={{
        [theme.mq.small]: {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },

        [theme.mq.medium]: {
          gridTemplateColumns: 'repeat(4, 1fr)',
        },
      }}
    >
      {items.map((item) => (
        <ListItem
          css={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          key={item.label}
        >
          <a
            css={[
              refineLinkStyles(theme),
              {
                height: theme.height['2xsmall'],
                lineHeight: `${
                  theme.height['2xsmall'] -
                  StylesResourcesVariables.refine.borderSize * 2
                }px`,
              },
            ]}
            className={clsx({
              [theme.settings.classes.active]: item.isRefined,
            })}
            href={createURL(item.value)}
            onClick={(event) => {
              event.preventDefault()
              refine(item.value)
            }}
            title={`${item.isRefined ? 'Remove' : 'Add'} ${item.label}`}
          >
            {item.label}
            {item.isRefined && (
              <Icon
                icon="close"
                size="small"
                css={{
                  position: 'absolute',
                  top: '50%',
                  right: StylesIconVariables.small,
                  transform: 'translateY(-50%)',
                }}
              />
            )}
          </a>
        </ListItem>
      ))}
    </List>
  )
}

TopicsRefinementList.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
}

export const CustomTopicsRefinementList = connectRefinementList(
  TopicsRefinementList
)
